import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Accordion, AccordionItem, Chip } from '@nextui-org/react';
import { runClaimIntakeOperation, type ClaimIntakeSession } from './claimIntakeAgentApi';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast } from '../toast/CtyToast';
import { ClaimSubmitErrorModal, type ClaimSubmitErrorModalContent } from './ClaimSubmitErrorModal';
import { ClaimSubmitProgressOverlay } from './ClaimSubmitProgressOverlay';
import { ClaimSubmitSuccessModal } from './ClaimSubmitSuccessModal';
import {
  isClaimSubmitWorkflowErrorResponse,
  getClaimSubmitUserMessage,
  parseClaimSubmitMutationError,
  parseClaimSubmitWorkflowError,
  readCreateClaimClaimNumber,
} from './claimSubmitErrorUtils';
import {
  getClaimRequests,
  getRequestLineDisplayRows,
  isRequest3CsComplete,
  isTireProductType,
  isVehicleTripIntakeComplete,
  formatRepairOrderDateDisplay,
  type ClaimGenerationReviewData,
  type ClaimRepairEntry,
  type ContractData,
} from './claimIntakeUtils';
import { claimObjectHasPartAndLaborLines } from './claimScoreFormState';
import { type ClaimCustomerData } from './buildUnifiedClaimPayload';
import { formatAmountDisplay } from './buildUnifiedClaimPayload';
import { mergeClaimObjectWithFormEdits } from './mergeClaimObjectFormEdits';
import {
  resolveDocumentFileSources,
  type AdditionalSourceItem,
} from './additionalInformationUpload';
import type { VehicleNotesFormState } from './vehicleNotesUtils';
import { VehicleNotesReviewSection } from './VehicleNotesReviewSection';
import type { PreviousClaimsState } from './previousClaimsApi';
import {
  ContractLookupReviewSection,
  VehicleClaimsReviewSections,
} from './claimIntakeReadOnlySections';


function labelArrivalMethod(v: string): string {
  if (v === 'driven') return 'Driven In';
  if (v === 'towed') return 'Towed';
  if (!v) return '—';
  return v;
}

function labelYesNo(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  if (!v) return '—';
  return v;
}

function labelReimbursement(v: string): string {
  if (v === 'Check') return 'Check';
  if (v === 'CONNEXPAY') return 'Credit Card';
  if (!v) return '—';
  return v;
}

function dashCell(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v.trim() === '' ? '—' : v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '—';
}

function getClaimSubmitBlockedMessage(
  vehicleTripComplete: boolean,
  claimObject: Record<string, unknown> | undefined
): string {
  if (vehicleTripComplete === false) {
    return 'Complete vehicle trip details on the Repairs step.';
  }
  if (claimObjectHasPartAndLaborLines(claimObject) === false) {
    return 'Claim must include at least one labor or part line from claim generation.';
  }
  return 'Submit is not available yet.';
}

const CLAIM_NUMBER_SUCCESS_MESSAGE = 'The claim has been submitted.';

function buildCustomerForSubmit(form: {
  customerName: string;
  customerPhone: string;
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerPostalCode: string;
  customerCountryCode: string;
}): ClaimCustomerData {
  return {
    name: form.customerName.trim(),
    phone_no: form.customerPhone.trim(),
    address: {
      address1: form.customerAddress1.trim(),
      address2: form.customerAddress2.trim(),
      city: form.customerCity.trim(),
      state: form.customerState.trim(),
      postal_code: form.customerPostalCode.trim(),
      country_code: form.customerCountryCode.trim(),
    },
  };
}

type ClaimSubmitResult =
  | { ok: true; claimNumber: string }
  | { ok: false; modal: ClaimSubmitErrorModalContent };

async function executeClaimSubmit(params: {
  session: ClaimIntakeSession;
  token: string;
  agentsBaseUrl?: string;
  submitSourceId?: string;
  aiworkerUsageId?: string;
  claimObjectForSubmit: Record<string, unknown>;
}): Promise<ClaimSubmitResult> {
  try {
    const { data } = await runClaimIntakeOperation({
      session: params.session,
      event: 'saveclaim',
      userInput: {
        source_id: params.submitSourceId,
        payload: {
          claim: params.claimObjectForSubmit,
          isValid: 'true',
        },
      },
      token: params.token,
      baseUrl: params.agentsBaseUrl,
      ...(params.aiworkerUsageId ? { aiworkerUsageId: params.aiworkerUsageId } : {}),
    });

    if (isClaimSubmitWorkflowErrorResponse(data)) {
      return {
        ok: false,
        modal: {
          ...parseClaimSubmitWorkflowError(data),
          heading: 'Could not submit this claim',
        },
      };
    }

    const claimNumber = readCreateClaimClaimNumber(data);
    if (!claimNumber) {
      const parsed = parseClaimSubmitWorkflowError(data);
      return {
        ok: false,
        modal: {
          heading: 'Could not submit this claim',
          summary: parsed.summary || (parsed.details.length > 0 ? '' : 'Submission did not return a claim number.'),
          details: parsed.details,
          ...(parsed.claimNumber ? { claimNumber: parsed.claimNumber } : {}),
        },
      };
    }

    return { ok: true, claimNumber };
  } catch (err: unknown) {
    const parsed = parseClaimSubmitMutationError(err);
    return {
      ok: false,
      modal: {
        heading: 'Could not submit this claim',
        summary: parsed.summary,
        details: parsed.details,
        ...(parsed.claimNumber ? { claimNumber: parsed.claimNumber } : {}),
      },
    };
  }
}

function RepairRequestReviewCard({
  req,
  repairIndex,
  productType,
}: Readonly<{
  req: ReturnType<typeof getClaimRequests>[number];
  repairIndex: number;
  productType: string;
}>) {
  const lines = getRequestLineDisplayRows(req);
  const failureCode = dashCell(req.failure_loss_code);
  const failureCodeIsDash = failureCode === '—';
  const isTireProduct = isTireProductType(productType);

  return (
    <div className="border border-default-200 rounded-lg overflow-hidden">
      <div className="bg-default-100 px-3 py-2 border-b border-default-200 flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold text-default-500 uppercase tracking-wide">
          Repair #{repairIndex + 1}
        </p>
        {failureCodeIsDash ? null : (
          <Chip size="sm" variant="flat" className="text-[10px] font-bold">
            {failureCode}
          </Chip>
        )}
        <Chip
          size="sm"
          color={isRequest3CsComplete(req) ? 'success' : 'warning'}
          variant="flat"
          className="text-[10px] font-bold"
        >
          {isRequest3CsComplete(req) ? '3Cs Complete' : '3Cs Incomplete'}
        </Chip>
      </div>
      <div className="p-3 sm:p-4 space-y-3 text-sm">
        <div>
          <p className="text-xs text-default-400">Complaint</p>
          <p className="text-foreground whitespace-pre-wrap">{dashCell(req.complaint_description)}</p>
        </div>
        <div>
          <p className="text-xs text-default-400">Cause</p>
          <p className="text-foreground whitespace-pre-wrap">{dashCell(req.cause_description)}</p>
        </div>
        <div>
          <p className="text-xs text-default-400">Correction</p>
          <p className="text-foreground whitespace-pre-wrap">{dashCell(req.corrective_action_description)}</p>
        </div>
        {isTireProduct ? (
          <div>
            <p className="text-xs text-default-400">Tire Tread</p>
            <p className="text-foreground">{dashCell(req.tiretread)}</p>
          </div>
        ) : null}
      </div>
      <div className="overflow-x-auto border-t border-default-200">
        <table className="w-full text-sm text-default-700">
          <thead>
            <tr className="bg-content2/80 text-left text-xs font-semibold uppercase tracking-wide text-default-500 border-b border-default-200">
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2 whitespace-nowrap">Part #</th>
              <th className="px-3 py-2 whitespace-nowrap">Type</th>
              <th className="px-3 py-2 whitespace-nowrap">Qty / Hours (hrs)</th>
              <th className="px-3 py-2 whitespace-nowrap">Amount ($)</th>
              <th className="px-3 py-2 whitespace-nowrap">Loss Code</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-default-400">
                  No labor or part lines for this repair.
                </td>
              </tr>
            ) : (
              lines.map((row) => (
                <tr key={row.lineType} className="border-b border-default-200 last:border-0 align-top">
                  <td className="px-3 py-2">{row.description || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.partNumber}</td>
                  <td className="px-3 py-2 whitespace-nowrap capitalize">{row.lineType}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.quantity || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatAmountDisplay(row.amount) || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.lossCode || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShopAndDocsTabPanel({
  arrivalMethod,
  atServiceCenter,
  reimbursement,
  paymentEmail = '',
  contractNumber,
  productType,
  mileageIn,
  invoiceNumber,
  repairOrderDate,
  vehicleVin,
  customerName,
  customerPhone,
  customerAddress1,
  customerAddress2,
  customerCity,
  customerState,
  customerPostalCode,
  customerCountryCode,
  uploadedFiles,
  additionalInfoFiles = [],
  servicerName,
  servicerPhone,
  servicerContactName = '',
  servicerMobile = '',
  wantText = '',
  contactEmail,
  laborRate,
  servicerAddress1,
  servicerAddress2,
  servicerCity,
  servicerState,
  servicerPostalCode,
  servicerCountryCode,
  servicerClassification,
  vehicleNotes,
  isPremierTenant = false,
}: Readonly<{
  arrivalMethod: string;
  atServiceCenter: string;
  reimbursement: string;
  paymentEmail?: string;
  contractNumber: string;
  productType: string;
  mileageIn: string;
  invoiceNumber: string;
  repairOrderDate: string;
  vehicleVin: string;
  customerName: string;
  customerPhone: string;
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerPostalCode: string;
  customerCountryCode: string;
  uploadedFiles: readonly File[];
  additionalInfoFiles?: readonly File[];
  servicerName: string;
  servicerPhone: string;
  servicerContactName?: string;
  servicerMobile?: string;
  wantText?: string;
  contactEmail: string;
  laborRate: string;
  servicerAddress1: string;
  servicerAddress2: string;
  servicerCity: string;
  servicerState: string;
  servicerPostalCode: string;
  servicerCountryCode: string;
  servicerClassification: string;
  vehicleNotes: VehicleNotesFormState;
  isPremierTenant?: boolean;
}>) {
  return (
    <div className="space-y-4">
      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Vehicle At Shop</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
          <div>
            <p className="text-xs text-default-400 mb-0.5">How Did The Vehicle Arrive At The Shop?</p>
            <p className="text-sm font-semibold text-foreground">{labelArrivalMethod(arrivalMethod)}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Is The Vehicle At The Service Center?</p>
            <p className="text-sm font-semibold text-foreground">{labelYesNo(atServiceCenter)}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-xs text-default-400 mb-0.5">How would you like to be paid?</p>
            <p className="text-sm font-semibold text-foreground">{labelReimbursement(reimbursement)}</p>
          </div>
          {reimbursement === 'CONNEXPAY' ? (
            <div className="sm:col-span-2">
              <p className="text-xs text-default-400 mb-0.5">Payment Email</p>
              <p className="text-sm font-semibold text-foreground">{paymentEmail.trim() || '—'}</p>
            </div>
          ) : null}
        </div>
      </div>

      {isPremierTenant ? null : (
      <VehicleNotesReviewSection
        vehicleNotes={vehicleNotes}
        hideFluidAndCodes={isTireProductType(productType)}
      />
      )}

      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Invoice & Contract</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
          <div>
            <p className="text-xs text-default-400 mb-0.5">Contract Number</p>
            <p className="text-sm font-semibold text-foreground">{contractNumber.trim() || '—'}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-xs text-default-400 mb-0.5">Product Type</p>
            <p className="text-sm font-semibold text-foreground">{productType.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Mileage In</p>
            <p className="text-sm font-semibold text-foreground">{mileageIn.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Invoice Number</p>
            <p className="text-sm font-semibold text-foreground">{invoiceNumber.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Repair Order Date</p>
            <p className="text-sm font-semibold text-foreground">{formatRepairOrderDateDisplay(repairOrderDate)}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Vehicle Identification Number (VIN)</p>
            <p className="text-sm font-semibold text-foreground">{vehicleVin.trim() || '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Customer</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
          <div>
            <p className="text-xs text-default-400 mb-0.5">Customer Name</p>
            <p className="text-sm font-semibold text-foreground">{customerName.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Customer Phone</p>
            <p className="text-sm font-semibold text-foreground">{customerPhone.trim() || '—'}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-xs text-default-400 mb-0.5">Address Line 1</p>
            <p className="text-sm font-semibold text-foreground">{customerAddress1.trim() || '—'}</p>
          </div>
          {customerAddress2.trim() ? (
            <div className="sm:col-span-2">
              <p className="text-xs text-default-400 mb-0.5">Address Line 2</p>
              <p className="text-sm font-semibold text-foreground">{customerAddress2.trim()}</p>
            </div>
          ) : null}
          <div>
            <p className="text-xs text-default-400 mb-0.5">City</p>
            <p className="text-sm font-semibold text-foreground">{customerCity.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">State</p>
            <p className="text-sm font-semibold text-foreground">{customerState.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Postal Code</p>
            <p className="text-sm font-semibold text-foreground">{customerPostalCode.trim() || '—'}</p>
          </div>          
        </div>
      </div>

      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Uploaded Documents</h3>
        {uploadedFiles.length > 0 ? (
          <ul className="space-y-2">
            {uploadedFiles.map((file, i) => (
              <li key={`${file.name}-${file.lastModified}-${i}`} className="flex items-center gap-2 text-sm text-default-700">
                <Icon icon="mdi:file-outline" width={18} className="text-default-400 shrink-0" />
                <span className="truncate">{file.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-default-400">No documents uploaded.</p>
        )}
      </div>

      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Additional Information</h3>
        {additionalInfoFiles.length > 0 ? (
          <ul className="space-y-2">
            {additionalInfoFiles.map((file, i) => (
              <li key={`addl-${file.name}-${file.lastModified}-${i}`} className="flex items-center gap-2 text-sm text-default-700">
                <Icon icon="mdi:paperclip" width={18} className="text-default-400 shrink-0" />
                <span className="truncate">{file.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-default-400">No additional files uploaded.</p>
        )}
      </div>

      <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-foreground">Service Center Information</h3>
          {servicerClassification.trim() ? (
            <Chip size="sm" color="primary" variant="flat" className="text-[10px] font-semibold px-2">
              {servicerClassification.trim()}
            </Chip>
          ) : null}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
          <div>
            <p className="text-xs text-default-400 mb-0.5">Servicer Name</p>
            <p className="text-sm font-semibold text-foreground">{servicerName.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Contact Name</p>
            <p className="text-sm font-semibold text-foreground">{servicerContactName.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Servicer Phone Number</p>
            <p className="text-sm font-semibold text-foreground">{servicerPhone.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Mobile Number</p>
            <p className="text-sm font-semibold text-foreground">{servicerMobile.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Do you want us to text you?</p>
            <p className="text-sm font-semibold text-foreground">{labelYesNo(wantText)}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Contact Email</p>
            <p className="text-sm font-semibold text-foreground">{contactEmail.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Labor Rate ($/Hour)</p>
            <p className="text-sm font-semibold text-foreground">
              {laborRate.trim() ? formatAmountDisplay(laborRate) : '—'}
            </p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-xs text-default-400 mb-0.5">Address Line 1</p>
            <p className="text-sm font-semibold text-foreground">{servicerAddress1.trim() || '—'}</p>
          </div>
          {servicerAddress2.trim() ? (
            <div className="sm:col-span-2">
              <p className="text-xs text-default-400 mb-0.5">Address Line 2</p>
              <p className="text-sm font-semibold text-foreground">{servicerAddress2.trim()}</p>
            </div>
          ) : null}
          <div>
            <p className="text-xs text-default-400 mb-0.5">City</p>
            <p className="text-sm font-semibold text-foreground">{servicerCity.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">State</p>
            <p className="text-sm font-semibold text-foreground">{servicerState.trim() || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-default-400 mb-0.5">Postal Code</p>
            <p className="text-sm font-semibold text-foreground">{servicerPostalCode.trim() || '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RepairsReviewSection({
  claimRequests,
  productType,
}: Readonly<{
  claimRequests: ReturnType<typeof getClaimRequests>;
  productType: string;
}>) {
  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">Repairs (Requests)</h3>
      <p className="text-sm text-default-500 mb-4">
        3Cs and line items from the generated claim object. Review before submitting.
      </p>

      {claimRequests.length === 0 ? (
        <p className="text-sm text-default-400">No repair requests on this claim.</p>
      ) : (
        <div className="space-y-8">
          {claimRequests.map((req, ri) => (
            <RepairRequestReviewCard
              key={req.lineType as string}
              req={req}
              repairIndex={ri}
              productType={productType}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const CLAIM_SUBMIT_DISCLAIMER =
  'By submitting this claim, I acknowledge and certify that the information provided is true, accurate, and complete to the best of my knowledge. I further confirm that the vehicle will remain at the service center pending claim authorization, unless removed at the owner\'s direction or due to circumstances outside the service center\'s reasonable control.';

function SubmitClaimFooter({
  claimGenerationReview,
  vehicleTripComplete,
  canSubmitClaim,
  submittedClaimNumber,
  isClaimSubmitting,
  isClaimScoreLoading,
  waitForClaimScore,
  onSubmit,
}: Readonly<{
  claimGenerationReview: ClaimGenerationReviewData | null;
  vehicleTripComplete: boolean;
  canSubmitClaim: boolean;
  submittedClaimNumber: string | null;
  isClaimSubmitting: boolean;
  isClaimScoreLoading: boolean;
  waitForClaimScore: boolean;
  onSubmit: () => void;
}>) {
  const scoreBlocksSubmit = waitForClaimScore && isClaimScoreLoading;
  const isSubmitted = submittedClaimNumber != null;
  const submitDisabled = isSubmitted || canSubmitClaim === false || isClaimSubmitting || scoreBlocksSubmit;
  const showSubmitBlockedHint =
    canSubmitClaim === false && !isSubmitted && !scoreBlocksSubmit;

  return (
    <div className="flex flex-col gap-3 pt-2">
      {!isSubmitted ? (
        <p className="text-sm text-default-600 leading-relaxed">
          {CLAIM_SUBMIT_DISCLAIMER}
        </p>
      ) : null}

      <div className="flex flex-col items-end gap-2">
        {showSubmitBlockedHint ? (
          <p className="text-sm text-amber-800 dark:text-amber-400 text-right max-w-md">
            {getClaimSubmitBlockedMessage(
              vehicleTripComplete,
              claimGenerationReview?.claimObject
            )}
          </p>
        ) : null}

        <CtyButton
          color="primary"
          className="font-semibold"
          isLoading={isClaimSubmitting}
          isDisabled={submitDisabled}
          onPress={onSubmit}
        >
          {isSubmitted ? 'Submitted' : 'Submit Claim'}
        </CtyButton>
        {isSubmitted ? (
          <p className="text-sm font-semibold text-green-700 dark:text-green-400 text-right">{CLAIM_NUMBER_SUCCESS_MESSAGE}</p>
        ) : null}
        {!isSubmitted && scoreBlocksSubmit ? (
          <p className="text-sm text-right max-w-md text-default-500">
            Claim score is updating. Please wait before submitting.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function ReviewTab({
  contract,
  arrivalMethod,
  atServiceCenter,
  reimbursement,
  paymentEmail = '',
  vehicleNotes,
  uploadedFiles,
  additionalInfoFiles = [],
  servicerName,
  servicerPhone,
  servicerContactName = '',
  servicerMobile = '',
  wantText = '',
  servicerAddress1,
  servicerAddress2,
  servicerCity,
  servicerState,
  servicerPostalCode,
  servicerCountryCode,
  servicerClassification,
  contactEmail,
  laborRate,
  mileageIn,
  invoiceNumber,
  repairOrderDate,
  vehicleVin,
  customerName,
  customerPhone,
  customerAddress1,
  customerAddress2,
  customerCity,
  customerState,
  customerPostalCode,
  customerCountryCode,
  contractNumber,
  dealerNumber,
  productType,
  repairs,
  claimGenerationReview,
  onClaimObjectUpdated,
  onBack,
  session,
  agentsBaseUrl,
  authToken,
  submitSourceId,
  claimScoreEnrichedForm,
  isClaimScoreLoading = false,
  waitForClaimScore = false,
  aiworkerUsageId,
  customerFromApi,
  previousClaims,
  voiceSubmitNonce = 0,
  additionalSources = [],
  documentFileSources = [],
  isPremierTenant = false,
}: Readonly<{
  contract: ContractData | null;
  arrivalMethod: string;
  atServiceCenter: string;
  reimbursement: string;
  paymentEmail?: string;
  vehicleNotes: VehicleNotesFormState;
  uploadedFiles: readonly File[];
  additionalInfoFiles?: readonly File[];
  servicerName: string;
  /** Servicer phone — populated from 3csmapper response, user-editable. */
  servicerPhone: string;
  servicerContactName?: string;
  servicerMobile?: string;
  wantText?: string;
  /** Servicer address — populated from 3csmapper response, user-editable. */
  servicerAddress1: string;
  servicerAddress2: string;
  servicerCity: string;
  servicerState: string;
  servicerPostalCode: string;
  servicerCountryCode: string;
  servicerClassification: string;
  contactEmail: string;
  laborRate: string;
  mileageIn: string;
  invoiceNumber: string;
  repairOrderDate: string;
  vehicleVin: string;
  customerName: string;
  customerPhone: string;
  /** Customer address — pre-filled from contract, user-editable. */
  customerAddress1: string;
  customerAddress2: string;
  customerCity: string;
  customerState: string;
  customerPostalCode: string;
  customerCountryCode: string;
  contractNumber: string;
  dealerNumber: string;
  productType: string;
  repairs: readonly ClaimRepairEntry[];
  claimGenerationReview: ClaimGenerationReviewData | null;
  onClaimObjectUpdated: (claimObject: Record<string, unknown>) => void;
  onBack: () => void;
  session: ClaimIntakeSession | null;
  agentsBaseUrl?: string;
  authToken?: string | null;
  submitSourceId?: string;
  claimScoreEnrichedForm?: Record<string, unknown> | null;
  isClaimScoreLoading?: boolean;
  /** Logged-in users see the score sidebar — wait for score before claim submit. */
  waitForClaimScore?: boolean;
  /** The session's aiworker_usage_id — passed to saveclaim for unified backend logging. */
  aiworkerUsageId?: string;
  /** Customer data returned by 3csmapper — used in submission payload; if absent falls back to form fields. */
  customerFromApi?: ClaimCustomerData | null;
  previousClaims?: PreviousClaimsState;
  /** Incremented by voice agent `submit_claim` to trigger saveclaim. */
  voiceSubmitNonce?: number;
  additionalSources?: AdditionalSourceItem[];
  documentFileSources?: AdditionalSourceItem[];
  /** Premier tenants skip vehicle notes; parent decides this once. */
  isPremierTenant?: boolean;
}>) {
  const [submitErrorModal, setSubmitErrorModal] = useState<ClaimSubmitErrorModalContent | null>(null);
  const [submitSuccessModalOpen, setSubmitSuccessModalOpen] = useState(false);
  const [isClaimSubmitting, setIsClaimSubmitting] = useState(false);
  const [submittedClaimNumber, setSubmittedClaimNumber] = useState<string | null>(null);

  const claimRequests = useMemo(
    () => getClaimRequests(claimGenerationReview?.claimObject),
    [claimGenerationReview?.claimObject]
  );

  const vehicleTripComplete = useMemo(
    () => isVehicleTripIntakeComplete(arrivalMethod, atServiceCenter, reimbursement),
    [arrivalMethod, atServiceCenter, reimbursement]
  );

  const scoreBlocksSubmit = waitForClaimScore && isClaimScoreLoading;

  const canSubmitClaim = useMemo(
    () =>
      !submittedClaimNumber &&
      !scoreBlocksSubmit &&
      Boolean(authToken?.trim()) &&
      Boolean(contract) &&
      Boolean(session) &&
      Boolean(claimGenerationReview) &&
      vehicleTripComplete &&
      claimObjectHasPartAndLaborLines(claimGenerationReview?.claimObject),
    [
      authToken,
      contract,
      session,
      claimGenerationReview,
      vehicleTripComplete,
      submittedClaimNumber,
      scoreBlocksSubmit,
    ]
  );

  const handleSubmitClaim = useCallback(async () => {
    if (canSubmitClaim === false) return;

    const token = authToken?.trim();
    if (!token || !session || !contract) return;

    const customerForPayload = buildCustomerForSubmit({
      customerName,
      customerPhone,
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerPostalCode,
      customerCountryCode,
    });

    const claimObjectForSubmit = claimGenerationReview?.claimObject
      ? mergeClaimObjectWithFormEdits(claimGenerationReview.claimObject, {
        repairs: [...repairs],
        laborRate,
        mileageIn,
        invoiceNumber,
        repairOrderDate,
        arrivalMethod,
        atServiceCenter,
        reimbursement,
        paymentEmail,
        vehicleNotes,
        customerName: customerForPayload.name,
        customerPhone: customerForPayload.phone_no,
        customerAddress: {
          address1: customerForPayload.address.address1,
          address2: customerForPayload.address.address2,
          city: customerForPayload.address.city,
          state: customerForPayload.address.state,
          postalCode: customerForPayload.address.postal_code,
          countryCode: customerForPayload.address.country_code,
        },
        servicerName,
        servicerPhone,
        servicerContactName,
        servicerMobile,
        wantText,
        contactEmail,
        servicerAddress: {
          address1: servicerAddress1,
          address2: servicerAddress2,
          city: servicerCity,
          state: servicerState,
          postalCode: servicerPostalCode,
          countryCode: servicerCountryCode,
        },
        dealerNumber,
        additionalSources,
        sources: resolveDocumentFileSources(documentFileSources, null),
      })
      : { customer: customerForPayload };

    setIsClaimSubmitting(true);
    try {
      const result = await executeClaimSubmit({
        session,
        token,
        agentsBaseUrl,
        submitSourceId,
        aiworkerUsageId,
        claimObjectForSubmit,
      });

      if (result.ok === false) {
        setSubmitErrorModal(result.modal);
        const generatedClaimNumber = result.modal.claimNumber?.trim();
        if (generatedClaimNumber) {
          setSubmittedClaimNumber(generatedClaimNumber);
        }
        CtyShowToast('error', getClaimSubmitUserMessage(result.modal));
        return;
      }

      setSubmittedClaimNumber(result.claimNumber);
      setSubmitSuccessModalOpen(true);
      // eslint-disable-next-line no-console -- debug: full claim number must not appear in UI
      console.log('[ClaimIntake] Generated claim number:', result.claimNumber);
    } finally {
      setIsClaimSubmitting(false);
    }
  }, [
    authToken,
    contract,
    session,
    agentsBaseUrl,
    submitSourceId,
    claimGenerationReview,
    canSubmitClaim,
    aiworkerUsageId,
    customerName,
    customerPhone,
    customerAddress1,
    customerAddress2,
    customerCity,
    customerState,
    customerPostalCode,
    customerCountryCode,
    repairs,
    laborRate,
    mileageIn,
    invoiceNumber,
    repairOrderDate,
    arrivalMethod,
    atServiceCenter,
    reimbursement,
    paymentEmail,
    vehicleNotes,
    servicerName,
    servicerPhone,
    servicerContactName,
    servicerMobile,
    wantText,
    contactEmail,
    servicerAddress1,
    servicerAddress2,
    servicerCity,
    servicerState,
    servicerPostalCode,
    servicerCountryCode,
    dealerNumber,
    additionalSources,
    documentFileSources,
  ]);

  useEffect(() => {
    if (!voiceSubmitNonce) return;
    void handleSubmitClaim();
  }, [voiceSubmitNonce, handleSubmitClaim]);

  return (
    <div className="space-y-4 sm:space-y-6" aria-busy={isClaimSubmitting}>
      <ClaimSubmitProgressOverlay isActive={isClaimSubmitting} />
      <ClaimSubmitErrorModal content={submitErrorModal} onClose={() => setSubmitErrorModal(null)} />
      <ClaimSubmitSuccessModal
        isOpen={submitSuccessModalOpen}
        claimNumber={submittedClaimNumber}
        showClaimNumber={waitForClaimScore}
        onClose={() => setSubmitSuccessModalOpen(false)}
        onStartNewClaim={() => globalThis.location.reload()}
      />

      <Accordion
        selectionMode="multiple"
        variant="splitted"
        className="px-0"
        defaultExpandedKeys={['contract-lookup']}
      >
        <AccordionItem key="contract-lookup" title="Contract Lookup" aria-label="Contract Lookup">
          <ContractLookupReviewSection contract={contract} />
        </AccordionItem>

        <AccordionItem key="vehicle-claims" title="Vehicle & Claims" aria-label="Vehicle and Claims">
          <VehicleClaimsReviewSections contract={contract} previousClaims={previousClaims} />
        </AccordionItem>

        <AccordionItem key="repairs-docs" title="Repairs & Docs" aria-label="Repairs and Docs">
          <div className="space-y-4">
            <ShopAndDocsTabPanel
              arrivalMethod={arrivalMethod}
              atServiceCenter={atServiceCenter}
              reimbursement={reimbursement}
              paymentEmail={paymentEmail}
              contractNumber={contractNumber}
              productType={productType}
              mileageIn={mileageIn}
              invoiceNumber={invoiceNumber}
              repairOrderDate={repairOrderDate}
              vehicleVin={vehicleVin}
              customerName={customerName}
              customerPhone={customerPhone}
              customerAddress1={customerAddress1}
              customerAddress2={customerAddress2}
              customerCity={customerCity}
              customerState={customerState}
              customerPostalCode={customerPostalCode}
              customerCountryCode={customerCountryCode}
              uploadedFiles={uploadedFiles}
              additionalInfoFiles={additionalInfoFiles}
              servicerName={servicerName}
              servicerPhone={servicerPhone}
              servicerContactName={servicerContactName}
              servicerMobile={servicerMobile}
              wantText={wantText}
              contactEmail={contactEmail}
              laborRate={laborRate}
              servicerAddress1={servicerAddress1}
              servicerAddress2={servicerAddress2}
              servicerCity={servicerCity}
              servicerState={servicerState}
              servicerPostalCode={servicerPostalCode}
              servicerCountryCode={servicerCountryCode}
              servicerClassification={servicerClassification}
              vehicleNotes={vehicleNotes}
              isPremierTenant={isPremierTenant}
            />
            {claimGenerationReview ? (
              <RepairsReviewSection
                claimRequests={claimRequests}
                productType={productType}
              />
            ) : (
              <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
                <p className="text-sm text-default-500">
                  Complete the previous step and proceed from repairs to load claim validation data.
                </p>
              </div>
            )}
          </div>
        </AccordionItem>
      </Accordion>

      <SubmitClaimFooter
        claimGenerationReview={claimGenerationReview}
        vehicleTripComplete={vehicleTripComplete}
        canSubmitClaim={canSubmitClaim}
        submittedClaimNumber={submittedClaimNumber}
        isClaimSubmitting={isClaimSubmitting}
        isClaimScoreLoading={isClaimScoreLoading}
        waitForClaimScore={waitForClaimScore}
        onSubmit={() => handleSubmitClaim()}
      />

      {!submittedClaimNumber && <div className="flex flex-wrap items-center gap-3 pt-2">
        <CtyButton
          variant="bordered"
          onPress={onBack}
          startContent={<Icon icon="mdi:arrow-left" width={18} />}
          className="font-semibold"
        >
          Back To Repairs
        </CtyButton>
      </div>}
    </div>
  );
}
